import React, { useEffect, useState } from 'react';
import { Box, Grid } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { FormikProps, useFormik } from 'formik';
import { useDispatch } from 'react-redux';
import { useAppSelector } from '../../redux/hooks';
import { FormValues, validationSchema } from '../../utils/validations/officeDetailsForm';
import { fetchCountryCode, saveOfficedetails } from '../../redux/booking/bookingSlice';
import StyledButton from '../../components/Common/StyledButton';
import ConsultationTypeSelector from '../../components/BookingSettings/CoachOfficeDetails/ConsultationSelecter';
import OfficeDetailsForm from '../../components/BookingSettings/CoachOfficeDetails/OfficeDetailsForm';
import OfficeDetailsSkeleton from '../../components/BookingSettings/CoachOfficeDetails/CoachOfficeDetailsLoading';
import createPermissionCheckers from '../../permissions-validation/createPermissionCheckers';
import { UserPermissionType } from '../../types/commonTypes';

function OfficeDetails() {
  const { t } = useTranslation();
  const { countryCode, coachConfigurations, saveOfficedetailsLoading, coachConfigurationsLoading } =
    useAppSelector((state) => state.booking);
  const coachId = useAppSelector((state) => state.coach.coach.id);

  const [isProvideOnlineConsultations, setIsProvideOnlineConsultations] = useState<boolean>(
    coachConfigurations.isProvideOnlineConsultations
  );
  const [isProvideInPersonConsultations, setIsProvideInPersonConsultations] = useState<boolean>(
    coachConfigurations.isProvideInPersonConsultations
  );
  const permissionCheckers = createPermissionCheckers();
  const { checkPermissions } = permissionCheckers;

  const dispatch = useDispatch();

  const initialValues: FormValues = {
    address: coachConfigurations.officeAddress || '',
    phoneNumber: coachConfigurations.officePhone,
    isAddressPublic: coachConfigurations.makeAddressPublic,
    isPhoneNumberPublic: coachConfigurations.makePhonePublic,
  };

  const formik: FormikProps<FormValues> = useFormik({
    initialValues,
    validationSchema,
    onSubmit: () => {},
  });

  useEffect(() => {
    if (!countryCode) {
      dispatch(fetchCountryCode());
    }
  }, [countryCode]);

  const handlePhoneNumberChange = (pNumber: string) => {
    formik.setFieldValue('phoneNumber', pNumber);
  };

  const clearForm = () => {
    formik.setFieldValue('address', '');
    formik.setFieldValue('phoneNumber', '');
    formik.setFieldValue('isAddressPublic', false);
    formik.setFieldValue('isPhoneNumberPublic', false);
  };

  const { isValid } = formik;

  const handleSave = () => {
    dispatch(
      saveOfficedetails({
        coachId,
        activeStep: -1,
        officeAddress: formik.values.address,
        officePhone: formik.values.phoneNumber || '',
        makeAddressPublic: formik.values.isAddressPublic,
        makePhonePublic: formik.values.isPhoneNumberPublic,
        isProvideInPersonConsultations,
        isProvideOnlineConsultations,
        isGeneralSettings: true,
      })
    );
  };

  useEffect(() => {
    if (coachConfigurations.isProvideInPersonConsultations) {
      setIsProvideInPersonConsultations(true);
    }
    if (coachConfigurations.isProvideOnlineConsultations) {
      setIsProvideOnlineConsultations(true);
    }
    if (coachConfigurations.officeAddress) {
      formik.setFieldValue('address', coachConfigurations.officeAddress);
    }
    if (coachConfigurations.officePhone) {
      formik.setFieldValue('phoneNumber', coachConfigurations.officePhone);
    }
    if (coachConfigurations.makeAddressPublic) {
      formik.setFieldValue('isAddressPublic', true);
    }
    if (coachConfigurations.makePhonePublic) {
      formik.setFieldValue('isPhoneNumberPublic', true);
    }
  }, [coachConfigurations]);

  if (coachConfigurationsLoading) {
    return <OfficeDetailsSkeleton />;
  }

  return (
    <Grid
      display="flex"
      flexDirection="column"
      justifyContent="center"
      sx={{
        backgroundColor: '#fff',
        borderRadius: '10px',
        padding: '15px',
        marginTop: '24px',
        maxWidth: '430px',
        boxShadow: '0px 4px 40px 0px #ffddc840',
      }}
    >
      <form onSubmit={formik.handleSubmit} noValidate>
        <ConsultationTypeSelector
          isProvideOnlineConsultations={isProvideOnlineConsultations}
          isProvideInPersonConsultations={isProvideInPersonConsultations}
          setIsProvideOnlineConsultations={setIsProvideOnlineConsultations}
          setIsProvideInPersonConsultations={setIsProvideInPersonConsultations}
          clearForm={clearForm}
          hasPermissions={checkPermissions(
            UserPermissionType.BOOKINGS_MODULE_MANAGE_COACH_CONFIGURATIONS
          )}
        />
        {isProvideInPersonConsultations && (
          <OfficeDetailsForm
            formik={formik}
            countryCode={countryCode}
            handlePhoneNumberChange={handlePhoneNumberChange}
            hasPermissions={checkPermissions(
              UserPermissionType.BOOKINGS_MODULE_MANAGE_COACH_CONFIGURATIONS
            )}
          />
        )}
        {checkPermissions(UserPermissionType.BOOKINGS_MODULE_MANAGE_COACH_CONFIGURATIONS) && (
          <Box
            display="flex"
            alignItems="center"
            justifyContent="flex-end"
            width="100%"
            pr={2}
            pl={2}
            pb={2}
            pt={1}
          >
            <StyledButton
              onClick={handleSave}
              paddingX={60}
              paddingY={14}
              loading={saveOfficedetailsLoading}
              disabled={
                (!isProvideOnlineConsultations && !isProvideInPersonConsultations) ||
                ((!isValid || formik.values.address === '' || !formik.values.address) &&
                  isProvideInPersonConsultations)
              }
            >
              {t('common.save')}
            </StyledButton>
          </Box>
        )}
      </form>
    </Grid>
  );
}

export default OfficeDetails;
